import { useCallback, useState } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Checkbox,
  FormLayout,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { GenerativeTextField } from "@shared/ui";
import { WebsiteField } from "@shared/ui";
import { Instructor } from "@features/events/types";

interface ProfileButtonCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

export default function ProfileButtonCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: ProfileButtonCardProps) {
  const { t } = useTranslation();

  const [showProfileButtonOverride, setShowProfileButtonOverride] = useState<
    boolean | null
  >(null);
  const showProfileButton =
    showProfileButtonOverride ?? Boolean(instructorData.customButtonLabel);

  const handleShowProfileButtonChange = useCallback(
    (value: boolean) => {
      setShowProfileButtonOverride(value);
      if (!value) {
        // When disabling, clear both fields
        onDataChange({
          customButtonLink: undefined,
          customButtonLabel: undefined,
        });
      }
    },
    [onDataChange]
  );

  const handleCustomButtonLinkChange = useCallback(
    (value: string) => {
      // Only update if profile button is enabled
      if (showProfileButton) {
        onDataChange({ customButtonLink: value });
      }
    },
    [onDataChange, showProfileButton]
  );

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h2">
            {t("instructorDetails.profileButton.title")}
          </Text>
        </InlineStack>

        <Text as="p" variant="bodyMd" tone="subdued">
          {t("instructorDetails.profileButton.description")}
        </Text>

        <BlockStack gap="300">
          <Checkbox
            label={t("instructorDetails.profileButton.showButton")}
            checked={showProfileButton}
            onChange={handleShowProfileButtonChange}
          />

          {showProfileButton && (
            <FormLayout>
              <GenerativeTextField
                id="customButtonLabel"
                placementId="event.event.section.title"
                label={t("instructorDetails.profileButton.buttonLabel")}
                value={instructorData.customButtonLabel ?? {}}
                onChange={(value) => onDataChange({ customButtonLabel: value })}
                type="textField"
                canGenerate={true}
                requiredIndicator
              />
              <WebsiteField
                label={t("instructorDetails.profileButton.linkUrl")}
                value={instructorData.customButtonLink || ""}
                onChange={handleCustomButtonLinkChange}
                error={fieldErrors.customButtonLink}
                placeholder={t("common.placeholders.url")}
              />
            </FormLayout>
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
